/**
 * LanguageSwitcher Component
 * 
 * A modern, interactive language switcher that allows users to change the site language.
 * Features a clean design with flag icons and smooth transitions.
 * 
 * Features:
 * - Detects current locale from Next.js router
 * - Smooth transition when changing languages
 * - Visual indication of the currently selected language
 * - Accessible design with proper ARIA attributes
 * - Optimized for both light and dark modes
 */

import React, { useState } from "react";
import { useRouter } from "next/router";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown } from 'lucide-react';

// Supported locales with their display information
const SUPPORTED_LOCALES = [
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "zh", label: "中文", flag: "🇨🇳" }
];

const LanguageSwitcher: React.FC = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  
  // Detect current locale from router.locale (Next.js i18n)
  const currentLocale = router.locale || "en";
  const currentLocaleInfo = SUPPORTED_LOCALES.find(locale => locale.code === currentLocale) || SUPPORTED_LOCALES[0];
  
  // Toggle dropdown open/closed
  const toggleDropdown = () => setIsOpen(!isOpen);
  
  // Change locale and keep current path/query
  const changeLocale = (newLocale: string) => {
    // Close dropdown
    setIsOpen(false);
    
    // Only navigate if the locale is different
    if (newLocale !== currentLocale) {
      // Next.js i18n will handle locale routing if configured in next.config.js
      router.push(router.asPath, router.asPath, { locale: newLocale, scroll: false });
    }
  };

  return (
    <div className="relative">
      {/* Current language button */}
      <button
        className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus-ring"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select language"
      >
        <span className="mr-1">{currentLocaleInfo.flag}</span>
        <span className="text-sm font-medium hidden sm:inline">{currentLocaleInfo.code.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute right-0 mt-2 w-40 rounded-lg shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <ul
              className="py-1"
              role="listbox"
              aria-label="Select language"
              aria-activedescendant={`language-${currentLocale}`}
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <li key={locale.code}>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 ${
                      locale.code === currentLocale
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => changeLocale(locale.code)}
                    role="option"
                    id={`language-${locale.code}`}
                    aria-selected={locale.code === currentLocale}
                  >
                    <span className="text-base">{locale.flag}</span>
                    <span>{locale.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
